// @ds/activity/CommentThread — presentational composite for a comment thread.
//
// Takes comments[] + callbacks. Owns the "show resolved" toggle state.
// Renders: pinned-amber-rail gutter, resolved toggle, comment list (via
// CommentCard slots), empty state, and a footer composer slot.

import React, { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { Comment } from "@shared/types/employee";
import { EmptyState } from "@ds/display/EmptyState";
import { Eyebrow } from "@ds/display/Eyebrow";
import { TabCount } from "@ds/display/TabCount";
import { TextValue } from "@ds/display/TextValue";
import { Box } from "@ds/layout/Box";
import { Inline } from "@ds/layout/Inline";
import { Stack } from "@ds/layout/Stack";

// ─── Inputs ──────────────────────────────────────────────────────────────────

export interface CommentThreadInput {
  comments: Comment[];
  showResolved: boolean;
  title?: string;
  testId?: string;
}

export interface CommentThreadOutput {
  onShowResolvedChange: (next: boolean) => void;
}

export interface CommentThreadActions {
  renderCard: (comment: Comment) => ReactNode;
  renderComposer: () => ReactNode;
}

export type CommentThreadProps = CommentThreadInput & CommentThreadOutput & CommentThreadActions;

// ─── Chevron icon for the resolved toggle ────────────────────────────────────

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      width={10}
      height={10}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {open
        ? <polyline points="6 9 12 15 18 9" />
        : <polyline points="9 18 15 12 9 6" />}
    </svg>
  );
}

// ─── Section header ──────────────────────────────────────────────────────────

function SectionHeader({ title, count }: { title: string; count?: number }) {
  return (
    <Box paddingX="lg" paddingY="md" divider="bottom">
      <Inline gap="md" align="center" justify="between">
        <Inline inline gap="sm" align="center">
          <Eyebrow>{title}</Eyebrow>
          {count !== undefined && count > 0 && (
            <TabCount value={count} />
          )}
        </Inline>
      </Inline>
    </Box>
  );
}

// ─── CommentThread ───────────────────────────────────────────────────────────

export function CommentThread({
  comments,
  showResolved,
  title,
  testId,
  onShowResolvedChange,
  renderCard,
  renderComposer,
}: CommentThreadProps) {
  const { t } = useTranslation();
  const sectionTitle = title ?? t("comments.comments");

  const visible = useMemo(() => {
    return comments
      .filter((c) => showResolved || !c.resolved_at)
      .sort((a, b) => {
        const aPinned = a.is_pinned && !a.resolved_at;
        const bPinned = b.is_pinned && !b.resolved_at;
        if (aPinned !== bPinned) return aPinned ? -1 : 1;
        return 0;
      });
  }, [comments, showResolved]);

  const resolvedCount = comments.filter((c) => !!c.resolved_at).length;
  const totalCount    = comments.length;

  return (
    <Box
      as="section"
      testId={testId ?? "comment-thread-root"}
      tone="primary"
      border
      shape="rounded-lg"
      scroll="hidden"
    >
      <SectionHeader title={sectionTitle} count={totalCount > 0 ? totalCount : undefined} />

      {/* Thread body. Prior asymmetric left padding (14 px for the amber-rail
          gutter) is normalized to `paddingX="lg"` (16 px) — accepted drift. */}
      <Box paddingX="lg" paddingY="lg">
        <Stack gap="sm">
          {resolvedCount > 0 && (
            <TextValue
              as="span"
              size="sm"
              tone="muted"
              testId={testId ? `${testId}-resolved-toggle` : "comment-thread-resolved-toggle"}
              onClick={() => onShowResolvedChange(!showResolved)}
            >
              <Inline as="span" inline gap="xs" align="center">
                <ChevronIcon open={showResolved} />
                {showResolved
                  ? t("comments.hide_resolved")
                  : `${t("comments.show_resolved")} (${resolvedCount})`}
              </Inline>
            </TextValue>
          )}

          {visible.length === 0 ? (
            <EmptyState
              size="sm"
              title={t("comments.messages.no_comments")}
              testId={testId ? `${testId}-empty` : "comment-thread-empty"}
            />
          ) : (
            visible.map((c) => (
              <React.Fragment key={c.id}>{renderCard(c)}</React.Fragment>
            ))
          )}

          <Box testId={testId ? `${testId}-composer` : "comment-thread-composer"}>
            {renderComposer()}
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}

// Uncontrolled helper: wraps CommentThread and manages showResolved internally.
export function CommentThreadUncontrolled(
  props: Omit<CommentThreadProps, "showResolved" | "onShowResolvedChange">,
) {
  const [showResolved, setShowResolved] = useState(false);
  return (
    <CommentThread
      {...props}
      showResolved={showResolved}
      onShowResolvedChange={setShowResolved}
    />
  );
}
